import React, { useEffect, useState } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import FileUploader from "../components/FileUploader"
import SummaryDisplay from "../components/SummaryDisplay"
import StickyHeader from "../components/StickyHeader"
// import { generateSummary } from "../utils/mockApi"
// Uncomment the following line to use the actual API
import { 
  uploadResumeFile, 
  generateSummary, 
  getCvById, 
  getSummaryByCvId,
  matchCvWithJobDescription,
  MatchResult
} from "../apis/api" 
import * as localStorageManager from "../utils/localStorageManager"

const DashboardPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const cvIdParam = searchParams.get('cv_id')
  const jdIdParam = searchParams.get('jd_id')
  const collectionIdParam = searchParams.get('collection_id')
  
  // Track if this is the first load to prevent initial API calls
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  
  // Track if the resume was recently clicked from summaries page
  const [isFromSummariesPage, setIsFromSummariesPage] = useState(false)
  
  // Initialize state from localStorage or URL parameters
  const [fileContent] = useState<string | null>("") // Initialize with empty string to ensure conditional rendering works
  const [summary, setSummary] = useState<string | null>(() => {
    // Prioritize URL params, then localStorage
    if (cvIdParam) {
      // Check if we have this CV's summary cached
      const cachedSummary = cvIdParam ? localStorageManager.getCachedSummary(cvIdParam) : null;
      return cachedSummary;
    }
    return localStorageManager.getSummary();
  })
  const [isProcessing, setIsProcessing] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isMatching, setIsMatching] = useState(false)
  const [matchResult, setMatchResult] = useState<MatchResult | null>(
    localStorageManager.getMatchResult()
  )

  const [pdfUrl, setPdfUrl] = useState<string | null>(() => {
    // Prioritize URL params, then localStorage
    if (cvIdParam) {
      // Check if we have this CV's PDF URL cached
      const cachedUrl = cvIdParam ? localStorageManager.getCachedPdfUrl(cvIdParam) : null;
      return cachedUrl;
    }
    
    // Only use stored URL if it hasn't expired
    return localStorageManager.getPdfUrl();
  })
  const [error, setError] = useState<string | null>(null)
  const [fileId, setFileId] = useState<string | null>(() => {
    // Prioritize URL params, then localStorage
    return cvIdParam || localStorageManager.getFileId();
  })
  const [collectionId, setCollectionId] = useState<string | null>(() => {
    // Prioritize URL params, then localStorage
    return collectionIdParam || localStorageManager.getCollectionId();
  })
  const [jdId, setJdId] = useState<string | null>(() => {
    // Prioritize URL params, then localStorage
    return jdIdParam || localStorageManager.getJobDescriptionId();
  })
  // Only set isLoading if we have a cvId and we're not on first load
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    // Clean up object URLs when component unmounts or when a new file is uploaded
    console.log("PDF URL changed:", pdfUrl);
    
    // Create a reference to the current URL for cleanup
    const currentPdfUrl = pdfUrl;
    
    return () => {
      // Only revoke object URLs (not http URLs)
      if (currentPdfUrl && currentPdfUrl.startsWith('blob:')) {
        console.log("Revoking object URL:", currentPdfUrl);
        URL.revokeObjectURL(currentPdfUrl);
      }
      // Note: We don't clear localStorage on unmount because we want to persist state
    }
  }, [pdfUrl])

  useEffect(() => {
    // Debug the PDF URL when it changes
    if (pdfUrl) {
      console.log("PDF URL set:", pdfUrl);
    }
  }, [pdfUrl]);
  
  // Store fileId and collectionId in localStorage when they change
  useEffect(() => {
    if (fileId) {
      localStorageManager.storeFileId(fileId);
    }
  }, [fileId]);

  useEffect(() => {
    if (collectionId) {
      localStorageManager.storeCollectionId(collectionId);
    }
  }, [collectionId]);
  
  // Store jdId in localStorage when it changes
  useEffect(() => {
    if (jdId) {
      localStorageManager.saveJobDescriptionId(jdId);
    }
  }, [jdId]);
  
  // Store summary in localStorage when it changes
  useEffect(() => {
    if (summary && fileId) {
      localStorageManager.storeSummary(fileId, summary);
    }
  }, [summary, fileId]);

  // Check if the stored URL is expired and reload if needed, but not on first load
  useEffect(() => {
    const checkAndReloadExpiredUrl = async () => {
      // Skip this check on first load to prevent unnecessary API calls
      if (isFirstLoad) {
        return;
      }
      
      // If we have a file ID and a PDF URL but the URL might be expired,
      if (fileId && pdfUrl && pdfUrl.startsWith('http')) {  // Only check HTTP URLs, not blob URLs
        if (localStorageManager.isPdfUrlExpired()) {
          console.log("PDF URL has expired, reloading...");
          
          // Clear the current URL but keep the file ID
          setPdfUrl(null);
          
          // The main loading effect will trigger again since pdfUrl is now null
        }
      }
    };
    
    checkAndReloadExpiredUrl();
  }, [fileId, pdfUrl, isFirstLoad]); // Include isFirstLoad in dependencies

  // Check if this is a navigation from summaries page
  useEffect(() => {
    // If we have a cvIdParam, it means we're likely coming from summaries page
    if (cvIdParam) {
      // Check if we have the PDF cached locally to prevent unnecessary API calls
      const hasCachedPdf = localStorageManager.getCachedPdfUrl(cvIdParam) !== null;
      
      // If we have the cache, we can set that we're from summaries page
      if (hasCachedPdf) {
        setIsFromSummariesPage(true);
      }
    }
  }, [cvIdParam]);

  // Load CV and summary from URL parameters if provided or from stored fileId
  useEffect(() => {
    const loadCvAndSummary = async () => {
      // Only proceed if not on first load OR if we have a cvIdParam
      // This prevents automatically fetching on first page load
      if (isFirstLoad && !cvIdParam) {
        setIsFirstLoad(false);
        return;
      }
      
      console.log("Checking if we need to load CV:", { cvIdParam, fileId, pdfUrl });
      
      // Determine if we need to fetch from API:
      // 1. If we have cvIdParam and no pdfUrl or expired pdfUrl
      // 2. If we have fileId but no pdfUrl or expired HTTP pdfUrl
      const needToFetch = 
        (cvIdParam && (!pdfUrl || localStorageManager.isPdfUrlExpired())) || 
        (!cvIdParam && fileId && (!pdfUrl || (pdfUrl && pdfUrl.startsWith('http') && localStorageManager.isPdfUrlExpired())));
      
      // Prevent multiple refreshes when clicking from summaries page
      if (isFromSummariesPage && pdfUrl) {
        setIsFromSummariesPage(false);
        return;
      }
      
      if (needToFetch) {
        const idToUse = cvIdParam || fileId;
        const collIdToUse = collectionIdParam || collectionId || "scoville";
        setIsLoading(true)
        setError(null)
        
        try {
          // Load the CV
          const cvUrlData = await getCvById(idToUse as string, collIdToUse)
          if (cvUrlData && cvUrlData.url) {
            // Store the actual URL in state
            setPdfUrl(cvUrlData.url)
            
            // Store URL in localStorage manager
            localStorageManager.storePdfUrl(idToUse as string, cvUrlData);
            
            if (cvIdParam) {
              setFileId(cvIdParam)
            }
            
            // Now fetch the summary if we don't already have it or if it's not cached
            const cachedSummary = localStorageManager.getCachedSummary(idToUse as string);
            if (!summary && !cachedSummary) {
              const summaryText = await getSummaryByCvId(idToUse as string, collIdToUse)
              if (summaryText) {
                setSummary(summaryText)
                localStorageManager.storeSummary(idToUse as string, summaryText);
              }
            } else if (cachedSummary && !summary) {
              setSummary(cachedSummary);
            }
          } else {
            setError("Unable to load the requested resume")
          }
        } catch (err) {
          console.error("Error loading CV:", err)
          setError(err instanceof Error ? err.message : String(err))
        } finally {
          setIsLoading(false)
          setIsFirstLoad(false);
        }
      } else {
        setIsFirstLoad(false);
      }
    }
    
    loadCvAndSummary()
  }, [cvIdParam, fileId, pdfUrl, collectionId, collectionIdParam, isFirstLoad, isFromSummariesPage, summary])
  // Added isFirstLoad and isFromSummariesPage to dependencies
  
  const handleFileUpload = async (file: File) => {
    setIsProcessing(true)
    setSummary(null)
    setError(null)
    
    // Ensure we're no longer in first load state
    setIsFirstLoad(false)
    
    // Also reset the fromSummaries flag if it's set
    if (isFromSummariesPage) {
      setIsFromSummariesPage(false)
    }

    // Clear previous state in localStorage
    localStorageManager.clearCurrentResumeData();
    
    // Revoke previous URL if exists
    if (pdfUrl && pdfUrl.startsWith('blob:')) {
      console.log("Revoking previous object URL:", pdfUrl);
      URL.revokeObjectURL(pdfUrl);
    }

    // First, display the PDF
    // Handle any file type - convert to PDF if needed or display directly
    try {
      // Create a URL for the file - works for PDF and other file types
      const localFileUrl = URL.createObjectURL(file)
      console.log("Created URL for preview:", localFileUrl);
      console.log("File mime type:", file.type);
      setPdfUrl(localFileUrl)
    } catch (err) {
      console.error("Error creating object URL for preview:", err)
    }
    
    console.log("Starting file upload to S3 for:", file.name);
    
    try {
      // Upload the file to S3 first
      const cv_and_collection_id = await uploadResumeFile(file, {
        resumeCollectionId: "scoville",
        targetContentType: "application/pdf",
        originalFilename: file.name
      })

      const cv_id = cv_and_collection_id[0]
      const collection_id = cv_and_collection_id[1]

      setFileId(cv_id)
      setCollectionId(collection_id)
      console.log("File uploaded successfully to S3", cv_id ? `with ID: ${cv_id}` : "")
    } catch (error) {
      console.error("Error handling file upload:", error)
      if (error instanceof Error) {
        setError(`Upload failed: ${error.message}`)
      } else {
        setError("Upload failed: An unknown error occurred")
      }
    } finally {
      setIsProcessing(false)
    }
  }

  const handleGenerateSummary = async () => {
    setError(null)
    setIsGenerating(true)

    try {
      if (!fileId) {
        throw new Error("File ID is missing. Please upload a file first.")
      }
      
      const result = await generateSummary(fileId, collectionId || "scoville")
      setSummary(result)
      
      // Store the summary in cache when it's generated
      if (result && fileId) {
        localStorageManager.storeSummary(fileId, result);
        
        // Update the summary in the summaries list if it exists
        // This will make sure the summaries page shows the latest summary
        const existingSummaries = localStorageManager.getAllSummaries();
        if (existingSummaries) {
          const summaryToUpdate = existingSummaries.find(s => s.cv_id === fileId);
          if (summaryToUpdate) {
            summaryToUpdate.summary = result;
            localStorageManager.updateSummaryInList(summaryToUpdate);
          }
        }
      }
    } catch (err) {
      console.error("Error generating summary:", err)
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setIsGenerating(false)
    }
  }

  const handleMatchWithCV = async () => {
    // Validate that we have a CV uploaded
    if (!fileId) {
      setError("No CV uploaded. Please upload a CV first.");
      return;
    }
    
    // Check if there's a saved job description
    const jobDescriptionText = localStorageManager.getJobDescriptionText();
    
    if (!jobDescriptionText || !jobDescriptionText.trim()) {
      // If no job description is available, navigate to the Job Description page
      navigate('/job-description');
      return;
    }
    
    // Ensure we have the latest job description ID
    const latestJobDescId = localStorageManager.getJobDescriptionId();
    if (latestJobDescId && latestJobDescId !== jdId) {
      // Update the jdId state if it's changed in localStorage
      setJdId(latestJobDescId);
    }
    
    // If we have both CV and job description, perform the matching
    setIsMatching(true);
    setError(null);
    
    try {
      // Get the job description ID - either from state or from localStorage
      const jobDescId = jdId || localStorageManager.getJobDescriptionId();
      
      console.log("Matching CV with job description:", {
        cvId: fileId,
        jdId: jobDescId,
        collectionId: collectionId || "scoville"
      });
      
      if (!jobDescId) {
        throw new Error("No job description ID found. Please upload a job description first.");
      }
      
      const result = await matchCvWithJobDescription(
        fileId,
        jobDescId,
        collectionId || "scoville"  
      );
      
      // Format the result for display
      const formattedResult = {
        score: result.score,
        details: result.details
      };

      setMatchResult(formattedResult);
      // Store match result in localStorage
      localStorageManager.saveMatchResult(result);
    } catch (err) {
      console.error("Error matching CV with job description:", err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsMatching(false);
    }
  };

  return (
    <>
      {/* Add the sticky header */}
      <StickyHeader title="Resume Analysis">
      </StickyHeader>
      
      <main className="pt-2 px-4 md:px-6 lg:px-8 overflow-y-auto">
        <div className="w-full mx-auto pb-10">
          {/* Always show the file uploader unless we're actively loading */}
          {isLoading ? (
            <div className="flex justify-center items-center p-8 bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading resume and summary...</span>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm p-8 md:p-8 border border-gray-200 max-w-3xl mx-auto">
              <FileUploader
                onFileUpload={handleFileUpload}
                isProcessing={isProcessing}
              />
            </div>
          )}
          
          {error && (
            <div className="bg-red-100 text-red-700 px-4 py-2 rounded mt-4 mb-4 border border-red-300">
              Error: {error}
            </div>
          )}
          
          {/* Show the summary display ONLY when we have a PDF URL (either from upload or from loading) */}
          {pdfUrl && (
            <div className="w-full mt-6">
              <SummaryDisplay
                fileContent={fileContent}
                summary={summary}
                isGenerating={isGenerating}
                isMatching={isMatching}
                onGenerateSummary={handleGenerateSummary}
                onMatchWithCV={handleMatchWithCV}
                matchResult={matchResult}
                pdfUrl={pdfUrl}
                layout="horizontal" // Enable horizontal layout for side-by-side display
              />
            </div>
          )}
        </div>
      </main>
    </>
  )
}

export default DashboardPage
